import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// Import React Query Client and Provider
import { QueryClient, QueryClientProvider } from 'react-query'; 
import { useAuth } from './contexts/AuthContext';
import './App.css';

// Layout Components
import MainLayout from './components/layout/MainLayout';

// Toast Container
import ToastContainer from './components/common/ToastContainer';

// Page Components
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MapPage from './pages/MapPage';
import MessagesPage from './pages/MessagesPage';
import ConnectionsPage from './pages/ConnectionsPage';
import ForumPage from './pages/ForumPage';
import TasksPage from './pages/TasksPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UserProfilePage from './pages/UserProfilePage/UserProfilePage';
import EditProfilePage from './pages/UserProfilePage/EditProfilePage';
import TopicDetailPage from './pages/TopicDetailPage';

// Create a client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes default stale time
      refetchOnWindowFocus: false, // Optional: disable refetch on window focus
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

const ProtectedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <MainLayout>
        {children}
      </MainLayout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    // Wrap the app with the QueryClientProvider
    <QueryClientProvider client={queryClient}>
      <ToastContainer />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        
        {/* Protected Routes - Wrapped with ProtectedLayout */}
        <Route path="/" element={<ProtectedLayout><HomePage /></ProtectedLayout>} />
        <Route path="/map" element={<ProtectedLayout><MapPage /></ProtectedLayout>} />
        <Route path="/connections" element={<ProtectedLayout><ConnectionsPage /></ProtectedLayout>} />
        <Route path="/messages" element={<ProtectedLayout><MessagesPage /></ProtectedLayout>} />
        <Route path="/messages/:conversationId" element={<ProtectedLayout><MessagesPage /></ProtectedLayout>} />
        <Route path="/tasks" element={<ProtectedLayout><TasksPage /></ProtectedLayout>} />
        <Route path="/forum" element={<ProtectedLayout><ForumPage /></ProtectedLayout>} />
        <Route path="/forum/topic/:topicId" element={<ProtectedLayout><TopicDetailPage /></ProtectedLayout>} />
        <Route 
          path="/profile" 
          element={
            <ProtectedLayout>
              <UserProfilePage />
            </ProtectedLayout>
          } 
        />
        <Route 
          path="/profile/edit" 
          element={
            <ProtectedLayout>
              <EditProfilePage /> 
            </ProtectedLayout>
          } 
        />
        <Route 
            path="/profile/:userId" 
            element={
                <ProtectedLayout>
                    <UserProfilePage />
                </ProtectedLayout>
            } 
        />
        {/* <Route path="/settings" element={<ProtectedLayout><SettingsPage /></ProtectedLayout>} /> */}
        {/* <Route path="/datasources" element={<ProtectedLayout><DataSourcesPage /></ProtectedLayout>} /> */}
        
        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
